import React from 'lib/react';
import PropTypes from 'prop-types';
import { useTranslation } from 'lib/translation';
import {
  EVChip,
  EVDeleteIcon,
  EVEditOutlined,
  EVGrid,
  EVIconButton,
  EVTypography,
  EVButton,
} from 'lib/components';
import { useDispatch, useSelector } from 'lib/redux';
import clsx from 'lib/clsx';
import { formatAddress } from 'features/order/Order.utils';
import {
  deleteMapPointAction, updateMapPointAddressAction, updateFocusedPointIndex, fetchPinAddress,
} from 'features/order/Order.actions';
import { LOADING_ADDRESS, ERROR_LOADING_ADDRESS } from 'features/placeOrder/OrderAddresses.constants';
import EditBuildingDetails from './EditBuildingDetails.component';
import BuildingDetailsStyle from './BuildingDetailsStyle';
import LoadingAddress from '../LoadingAddress.component';

function BuildingDetails(props) {
  const { t } = useTranslation();
  const {
    building, isEditing, setEditingBuildingId, buildingData, index,
  } = props;
  const isComplex = useSelector((state) => state.order.isComplex);
  const focusedPointIndex = useSelector((state) => state.orderAddresses.focusedPointIndex);
  const classes = BuildingDetailsStyle({ index, focusedPointIndex });
  const dispatch = useDispatch();

  const handleEditClick = () => setEditingBuildingId(index);

  const handleDelete = (e) => {
    e.stopPropagation();
    dispatch(deleteMapPointAction(index));
  };

  const handleUpdate = (updatedAddress) => {
    const { building: updatedBuildingId, ...address } = updatedAddress;
    dispatch(updateMapPointAddressAction(index, address, updatedBuildingId));
    setEditingBuildingId(null);
  };

  const handleFocusChange = (e) => {
    e.stopPropagation();
    dispatch(updateFocusedPointIndex(index));
  };

  if (isEditing) {
    return (
      <EditBuildingDetails
        setEditingBuildingId={setEditingBuildingId}
        handleUpdate={handleUpdate}
        buildingData={{
          ...buildingData,
          building: building || (isComplex ? String(index + 1) : ''),
        }}
        index={index}
      />
    );
  }

  const buildingID = building || index + 1;

  const isAddressLoading = buildingData?.address === LOADING_ADDRESS;
  const isErrorAddress = buildingData?.address === ERROR_LOADING_ADDRESS;
  const isFocused = focusedPointIndex === index;

  const handleFetchPinAddress = (e) => {
    e.stopPropagation();
    dispatch(fetchPinAddress(index));
  };

  const addressDiv = () => {
    if (isAddressLoading) return <LoadingAddress isFocused={isFocused} />;

    if (isErrorAddress) {
      return (
        <EVButton
          id={`tryAgain-${index}`}
          onClick={handleFetchPinAddress}
          className={clsx(classes.tryAgainButton)}
          variant="outlined"
        >
          {t('order_history__retry')}
        </EVButton>
      );
    }

    return (
      <EVTypography className={classes.buildingDetailsAddress}>
        {formatAddress(buildingData)}
      </EVTypography>
    );
  };

  return (
    <EVGrid
      container
      wrap="nowrap"
      className={clsx(classes.buildingDetailsContainer, classes.helperText, focusedPointIndex === index && 'focused')}
      onClick={handleFocusChange}
    >
      <EVGrid className={classes.buildingDetails}>
        <EVTypography display="inline" className={classes.helperText}>{t('BuildingId')}</EVTypography>
        {
          (building || isComplex)
          && <EVChip label={buildingID} className={classes.buildingIdChip} />
        }
        {addressDiv()}
      </EVGrid>
      <EVIconButton
        id={`edit-building-${index}`}
        className={classes.editOutlineButton}
        onClick={handleEditClick}
        aria-label={`Edit building ${buildingID}`}
      >
        <EVEditOutlined htmlColor="#009AC5" />
      </EVIconButton>
      {
        isComplex
        && (
        <EVIconButton
          id={`delete-building-${index}`}
          className={classes.editOutlineButton}
          onClick={handleDelete}
          aria-label={`Delete building ${buildingID}`}
        >
          <EVDeleteIcon htmlColor="#009AC5" style={{ marginLeft: 7, marginTop: 4 }} />
        </EVIconButton>
        )
      }
    </EVGrid>
  );
}

BuildingDetails.propTypes = {
  building: PropTypes.string.isRequired,
  buildingData: PropTypes.shape().isRequired,
  isEditing: PropTypes.bool.isRequired,
  setEditingBuildingId: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
};

export default BuildingDetails;
